var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import React, { useState } from 'react';
import { compiler } from 'markdown-to-jsx';
import { Input, Box, Copy, Button, Fade, Link, Dropdown } from '@honda-canada/design-system-react';
import { useTranslation } from 'react-i18next';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import themeStyles from './SignupForUpdates.styles';
import { useProvinces } from '../../utils/sitecoreContext';
import { EqualHeightElement } from '../../utils/components/EqualHeight';
import CustomErrorLabel from './CustomErrorLabel';
import SuccessUI from './SuccessUI';
import ErrorUI from './ErrorUI';
import useFormSubmit from './hooks/useFormSubmit';
var FormWrapper = themeStyles.apply(Box, 'FormWrapper');
var FieldsContainer = themeStyles.apply(Box, 'FieldsContainer');
var InputWrapper = themeStyles.apply(Box, 'InputWrapper');
var StyledInput = themeStyles.apply(Input, 'StyledInput');
var StyledSelect = themeStyles.apply(Dropdown, 'StyledSelect');
var RequiredFieldsCopy = themeStyles.apply(Copy, 'RequiredFieldsCopy');
var PromotionCopy = themeStyles.apply(Copy, 'PromotionCopy');
var PrivacyContainer = themeStyles.apply(Box, 'PrivacyContainer');
var FormFields = function (_a) {
    var legalDisclaimer = _a.legalDisclaimer, formName = _a.formName, pageUrl = _a.pageUrl, ctaButton = _a.ctaButton;
    var t = useTranslation().t;
    var provinces = useProvinces();
    var initialState = {
        firstName: '',
        lastName: '',
        email: '',
        postalCode: '',
        address: '',
        city: '',
        province: '',
    };
    var _b = useFormSubmit(), submitForm = _b.submitForm, isSuccess = _b.isSuccess, isError = _b.isError;
    var _c = useState(initialState), inputValues = _c[0], setInputValues = _c[1];
    var validateFields = Yup.object().shape({
        firstName: Yup.string().required("".concat(t('Shared.SignUp.firstNameError'))),
        lastName: Yup.string().required("".concat(t('Shared.SignUp.lastNameError'))),
        email: Yup.string()
            .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/, "".concat(t('Shared.SignUp.emailError')))
            .required("".concat(t('Shared.SignUp.emailError'))),
        postalCode: Yup.string()
            .matches(/^[A-Za-z]\d[A-Za-z]\d[A-Za-z]\d$/, "".concat(t('Shared.SignUp.postalCodeError')))
            .required("".concat(t('Shared.SignUp.postalCodeError'))),
        address: Yup.string(),
        city: Yup.string(),
        province: Yup.string(),
    });
    var handleFormSubmit = function (values) {
        setInputValues(values);
        var items = [
            { Name: 'FirstName', Value: values.firstName },
            { Name: 'LastName', Value: values.lastName },
            { Name: 'Email', Value: values.email },
            { Name: 'PostalCode', Value: values.postalCode },
            { Name: 'Address', Value: values.address },
            { Name: 'City', Value: values.city },
            { Name: 'Province', Value: inputValues.province },
        ];
        var payload = {
            FormName: formName,
            PageUrl: pageUrl,
            Items: items,
        };
        return submitForm(payload);
    };
    var handleInputChange = function (event, setFieldValue, validateField) {
        var _a = event.target, name = _a.name, value = _a.value;
        // Only allow alphabetic characters for firstName and lastName
        if ((name === 'firstName' || name === 'lastName') && /^[a-zA-Z]*$/.test(value)) {
            setInputValues(function (prevValues) {
                var _a;
                return (__assign(__assign({}, prevValues), (_a = {}, _a[name] = value, _a)));
            });
            setFieldValue(name, value);
            validateField(name);
        }
        else if (name !== 'firstName' && name !== 'lastName') {
            setInputValues(function (prevValues) {
                var _a;
                return (__assign(__assign({}, prevValues), (_a = {}, _a[name] = value, _a)));
            });
            setFieldValue(name, value);
            validateField(name);
        }
    };
    var handleBlur = function (event, setFieldTouched, validateField) {
        var name = event.target.name;
        setFieldTouched(name, true); // Mark the field as touched
        validateField(name); // Trigger validation for the specific field
    };
    if (isError) {
        return (React.createElement(Fade, { shouldAnimate: true, initialOpacity: 0 },
            React.createElement(ErrorUI, null)));
    }
    if (isSuccess) {
        return (React.createElement(Fade, { shouldAnimate: true, initialOpacity: 0 },
            React.createElement(SuccessUI, { ctaButton: ctaButton })));
    }
    var provinceOptions = provinces.map(function (province) { return ({
        value: province.name,
        text: province.displayName,
    }); });
    return (React.createElement(FormWrapper, { "data-testid": "cy-signup-for-updates-form" },
        React.createElement(RequiredFieldsCopy, { size: "regularCopy" }, t('Shared.SignUp.requiredFieldsLabel')),
        React.createElement(Formik, { initialValues: inputValues, validationSchema: validateFields, onSubmit: handleFormSubmit, validateOnChange: true, validateOnBlur: true }, function (_a) {
            var isSubmitting = _a.isSubmitting, setFieldValue = _a.setFieldValue, validateField = _a.validateField, setFieldTouched = _a.setFieldTouched, errors = _a.errors, touched = _a.touched;
            return (React.createElement(Form, null,
                React.createElement(FieldsContainer, null,
                    React.createElement(InputWrapper, null,
                        React.createElement(EqualHeightElement, null,
                            React.createElement(Copy, { size: "small", htmlFor: "firstName", "data-testid": "cy-first-name" }, t('Shared.SignUp.firstNameLabel')),
                            React.createElement(Field, { type: "firstName", name: "firstName", as: StyledInput, value: inputValues.firstName, onChange: function (e) {
                                    return handleInputChange(e, setFieldValue, validateField);
                                }, onBlur: function (e) { return handleBlur(e, setFieldTouched, validateField); }, "data-testid": "cy-first-name-input", hasError: !!(touched.firstName && errors.firstName) }),
                            React.createElement(ErrorMessage, { name: "firstName", render: function (msg) { return React.createElement(CustomErrorLabel, { testId: "cy-first-name-error" }, msg); } }))),
                    React.createElement(InputWrapper, null,
                        React.createElement(EqualHeightElement, null,
                            React.createElement(Copy, { size: "small", htmlFor: "lastName" }, t('Shared.SignUp.lastNameLabel')),
                            React.createElement(Field, { type: "lastName", name: "lastName", as: StyledInput, value: inputValues.lastName, onChange: function (e) {
                                    return handleInputChange(e, setFieldValue, validateField);
                                }, onBlur: function (e) { return handleBlur(e, setFieldTouched, validateField); }, hasError: !!(touched.lastName && errors.lastName), "data-testid": "cy-last-name-input" }),
                            React.createElement(ErrorMessage, { name: "lastName", render: function (msg) { return React.createElement(CustomErrorLabel, { testId: "cy-last-name-error" }, msg); } })))),
                React.createElement(FieldsContainer, null,
                    React.createElement(InputWrapper, null,
                        React.createElement(EqualHeightElement, null,
                            React.createElement(Copy, { size: "small", htmlFor: "email" }, t('Shared.SignUp.emailLabel')),
                            React.createElement(Field, { type: "email", name: "email", as: StyledInput, onChange: function (e) {
                                    return handleInputChange(e, setFieldValue, validateField);
                                }, hasError: !!(touched.email && errors.email), "data-testid": "cy-email-input" }),
                            React.createElement(ErrorMessage, { name: "email", render: function (msg) { return React.createElement(CustomErrorLabel, { testId: "cy-email-error" }, msg); } }))),
                    React.createElement(InputWrapper, null,
                        React.createElement(EqualHeightElement, null,
                            React.createElement(Copy, { size: "small", htmlFor: "postalCode", "data-testid": "cy-postal-code" }, t('Shared.SignUp.postalCodeLabel')),
                            React.createElement(Field, { type: "postalCode", name: "postalCode", as: StyledInput, onChange: function (e) {
                                    return handleInputChange(e, setFieldValue, validateField);
                                }, "data-testid": "cy-postal-code-input", hasError: !!(touched.postalCode && errors.postalCode) }),
                            React.createElement(ErrorMessage, { name: "postalCode", render: function (msg) { return React.createElement(CustomErrorLabel, { testId: "cy-postal-code-error" }, msg); } })))),
                React.createElement(Box, null,
                    React.createElement(PromotionCopy, { "data-testid": "cy-promotion-copy", size: "regularCopy" }, t('Shared.SignUp.promotionInfoText'))),
                React.createElement(FieldsContainer, null,
                    React.createElement(InputWrapper, null,
                        React.createElement(Copy, { size: "small", htmlFor: "address", "data-testid": "cy-address" }, t('Shared.SignUp.addressLabel')),
                        React.createElement(Field, { type: "address", name: "address", as: StyledInput, onChange: function (e) {
                                return handleInputChange(e, setFieldValue, validateField);
                            }, "data-testid": "cy-address-input" })),
                    React.createElement(InputWrapper, null,
                        React.createElement(Copy, { size: "small", htmlFor: "city", "data-testid": "cy-city" }, t('Shared.SignUp.cityLabel')),
                        React.createElement(Field, { type: "city", name: "city", as: StyledInput, onChange: function (e) {
                                return handleInputChange(e, setFieldValue, validateField);
                            }, "data-testid": "cy-city-input" })),
                    React.createElement(InputWrapper, null,
                        React.createElement(Copy, { size: "small", htmlFor: "province", "data-testid": "cy-province" }, t('Shared.SignUp.provinceLabel')),
                        React.createElement(StyledSelect, { dataTestId: "cy-province-select", name: "province", styling: "primary", onChange: function (value) {
                                return setInputValues(function (prevValues) { return (__assign(__assign({}, prevValues), { province: value })); });
                            }, options: provinceOptions, height: "auto", placeholderText: t('Shared.SignUp.provincePlaceholder'), value: inputValues.province }))),
                compiler(legalDisclaimer, {
                    forceBlock: true,
                    overrides: {
                        div: {
                            component: PrivacyContainer,
                            props: {
                                'data-testid': 'cy-disclaimer',
                            },
                        },
                        p: {
                            component: Copy,
                            props: {
                                size: 'legal',
                            },
                        },
                        a: {
                            component: Link,
                            props: {
                                styling: 'redUnderline',
                                fontSize: 'inherit',
                                'data-testid': 'cy-disclaimer-privacy-link',
                                target: '_blank',
                            },
                        },
                    },
                }),
                React.createElement(Box, null,
                    React.createElement(Button, { type: "submit", styling: "primary", disabled: isSubmitting, "data-testid": "cy-signup-cta" }, t('Shared.SignUp.signUpButton')))));
        })));
};
export default FormFields;
